import React, { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import {
  SaveButton,
  CancelButton,
  EditButton,
  DeleteButton
} from './ui/ActionButtons';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from './ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from './ui/table';
import { Badge } from './ui/badge';
import { Search, Filter, Calendar, CheckCircle, Clock, AlertTriangle } from 'lucide-react';
import { useAppData } from '../hooks/useAppData';
import * as XLSX from 'xlsx';
import * as FileSaver from 'file-saver';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import dayjs from 'dayjs';
import { TableFilterBar } from './ui/TableFilterBar';
import { TableExportMenu } from './ui/TableExportMenu';
import { TablePeriodSelector } from './ui/TablePeriodSelector';
import { DownloadReceiptButton } from './ui/DownloadReceiptButton';
import { DetalleComision } from '../utils/receiptTemplates';
import { matchesMonthYear, matchesYear } from '../utils/dateFilters';

// Agrega función utilitaria para formato Guatemala
function formatGTDate(dateStr: string) {
  if (!dateStr) return '-';
  const d = new Date(dateStr);
  if (isNaN(d.getTime())) return '-';
  return d.toLocaleDateString('es-GT', { day: '2-digit', month: '2-digit', year: 'numeric' });
}

export function ComisionesVendedoresTable({ lots, sellers }: { lots: any[]; sellers: any[] }) {
  // Filtros
  const currentYear = new Date().getFullYear();
  const [selectedVendedor, setSelectedVendedor] = useState('Todos');
  const [selectedMonth, setSelectedMonth] = useState('Todos');
  const [selectedYear, setSelectedYear] = useState(currentYear);
  const [modalOpen, setModalOpen] = useState(false);
  const [pagoComision, setPagoComision] = useState<any>(null);
  const [form, setForm] = useState({ numeroDocumento: '', transferencia: '', boleta: '', fecha: dayjs().format('YYYY-MM-DD') }); // boleta oculto en UI pero mantenido para compatibilidad
  const [search, setSearch] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const { comisiones, updateComision, addMovimiento, config } = useAppData();

  // Ordenar por createdAt descendente (más reciente primero)
  const comisionesOrdenadas = [...comisiones].sort((a, b) => {
    if (a.createdAt && b.createdAt) {
      if (a.createdAt < b.createdAt) return 1;
      if (a.createdAt > b.createdAt) return -1;
    }
    return 0;
  });

  // Filtros
  const uniqueVendedores = ['Todos', ...Array.from(new Set(comisionesOrdenadas.map(m => m.sellerId)))];
  const meses = ['Todos', 'Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio', 'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'];

  // Filtrado consistente con LotTable
  const filtered = comisionesOrdenadas.filter(mov => {
    const searchLower = search.toLowerCase();
    const matchesSearch =
      (mov.lotNumber || '').toLowerCase().includes(searchLower) ||
      (mov.buyerName || '').toLowerCase().includes(searchLower) ||
      (mov.sellerName || '').toLowerCase().includes(searchLower);

    // Filtrado por mes y año mejorado
    let matchesMonthFilter = true;
    if (selectedMonth === 'Todos') {
      // Si es "Todos" los meses, filtrar solo por año
      matchesMonthFilter = matchesYear(mov.saleDate, selectedYear);
    } else {
      // Si es un mes específico, filtrar por mes Y año
      matchesMonthFilter = matchesMonthYear(mov.saleDate, selectedMonth, selectedYear);
    }

    return (
      (selectedVendedor === 'Todos' || mov.sellerId === selectedVendedor) &&
      matchesSearch &&
      matchesMonthFilter
    );
  });

  // Antes de ordenar y filtrar, loguear los movimientos y su createdAt
  console.log('Movimientos para tabla de comisiones:', comisionesOrdenadas.map(m => ({ id: m.id, createdAt: m.createdAt, saleDate: m.saleDate, paid: m.paid })));

  // Guardar pago de comisión
  const handleGuardarPago = async () => {
    if (!form.numeroDocumento.trim() || !form.fecha.trim()) {
      setError('El No. Doc. del Banco y la fecha son obligatorios.');
      return;
    }
    setError(null);
    // Normalizar lotes a array seguro para ID y concepto
    let lotNumbers: string[] = [];
    let lotesArray: string[] = [];
    if (Array.isArray(pagoComision.lotNumbers)) {
      lotNumbers = pagoComision.lotNumbers.map((l: any) => l.toString());
    } else if (Array.isArray(pagoComision.lotes)) {
      lotNumbers = pagoComision.lotes.map((l: any) => l.toString());
      lotesArray = lotNumbers;
    } else if (typeof pagoComision.lotes === 'string' && pagoComision.lotes.trim() !== '') {
      lotNumbers = pagoComision.lotes.split(/,|-/).map((l: string) => l.trim());
      lotesArray = lotNumbers;
    } else if (typeof pagoComision.lotNumber === 'string' && pagoComision.lotNumber.trim() !== '') {
      lotNumbers = pagoComision.lotNumber.split(/,|-/).map((l: string) => l.trim());
      lotesArray = lotNumbers;
    }
    const customId = 'comision-' + (Array.isArray(lotNumbers) ? lotNumbers.sort().join('-') : '');
    const concepto = pagoComision.concepto || `Comisión por venta de lote(s) ${lotesArray.join(', ')} a ${pagoComision.comprador}`;
    try {
      // 1. Actualizar la comisión a pagada
      if (pagoComision.id) {
        await updateComision(pagoComision.id, {
          paid: true,
          paymentDate: form.fecha,
          transferencia: form.transferencia,
          recibo: form.numeroDocumento || form.boleta, // Usar numeroDocumento como principal, boleta como fallback para compatibilidad
          numeroDocumento: form.numeroDocumento
        });
      }
      // 2. Registrar movimiento de egreso
      const egresoCustomId = `${lotesArray.join('-')}-comisionpagada`;

      // Construir referencia con información completa (usar numeroDocumento como principal)
      const referencia = form.transferencia?.trim()
        ? `No. Doc.: ${form.numeroDocumento}, No. Autorización: ${form.transferencia}`
        : `No. Doc.: ${form.numeroDocumento}`;

      await addMovimiento({
        concepto: `Pago de comisión a ${pagoComision.sellerName || pagoComision.sellerId} por lote(s) ${lotesArray.join(', ')}`,
        responsable: 'Sistema',
        fecha: form.fecha,
        monto: parseFloat(pagoComision.commissionAmount || pagoComision.comision || 0),
        estado: 'Pagado',
        referencia: referencia,
        tipo: 'egreso',
        lotes: lotesArray,
        vendedor: pagoComision.sellerName || pagoComision.sellerId,
        comprador: pagoComision.buyerName,
        automatic: true,
        // Nuevos campos para match con banco
        numeroDocumento: form.numeroDocumento.trim(),
        transferencia: form.transferencia?.trim() || undefined
      }, egresoCustomId);
      setModalOpen(false);
      setPagoComision(null);
      setForm({ numeroDocumento: '', transferencia: '', boleta: '', fecha: dayjs().format('YYYY-MM-DD') });
      setSuccess('¡Pago de comisión registrado exitosamente!');
      setTimeout(() => setSuccess(null), 4000);
    } catch (err) {
      setError('Error al guardar el pago de comisión.');
      console.error('Error al guardar el pago de comisión:', err);
    }
  };

  // Exportar a Excel
  const exportToExcel = () => {
    const ws = XLSX.utils.json_to_sheet(comisionesOrdenadas.map(mov => ({
      Lotes: mov.lotNumber || '-',
      Vendedor: mov.sellerName || mov.sellerId || '-',
      'Fecha Venta': mov.saleDate || '-',
      Comprador: mov.buyerName || '-',
      Comisión: parseFloat(mov.commissionAmount || 0).toLocaleString(),
      Estado: mov.paid ? 'Pagada' : 'Pendiente',
      'Fecha Pago': mov.paymentDate || '-',
      'Recibo/Transferencia': '-',
    })));
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Comisiones');
    const wbout = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
    FileSaver.saveAs(new Blob([wbout], { type: 'application/octet-stream' }), 'comisiones.xlsx');
  };

  // Exportar a CSV
  const exportToCSV = () => {
    const ws = XLSX.utils.json_to_sheet(comisionesOrdenadas.map(mov => ({
      Lotes: mov.lotNumber || '-',
      Vendedor: mov.sellerName || mov.sellerId || '-',
      'Fecha Venta': mov.saleDate || '-',
      Comprador: mov.buyerName || '-',
      Comisión: parseFloat(mov.commissionAmount || 0).toLocaleString(),
      Estado: mov.paid ? 'Pagada' : 'Pendiente',
      'Fecha Pago': mov.paymentDate || '-',
      'Recibo/Transferencia': '-',
    })));
    const csv = XLSX.utils.sheet_to_csv(ws);
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    FileSaver.saveAs(blob, 'comisiones.csv');
  };

  // Exportar a PDF
  const exportToPDF = () => {
    const doc = new jsPDF();
    doc.text('Comisiones de Vendedores', 14, 16);
    autoTable(doc, {
      head: [[
        'Lote(s)', 'Vendedor', 'Fecha Venta', 'Comprador', 'Comisión', 'Estado', 'Fecha Pago', 'Recibo/Transferencia'
      ]],
      body: comisionesOrdenadas.map(mov => [
        mov.lotNumber || '-',
        mov.sellerName || mov.sellerId || '-',
        mov.saleDate || '-',
        mov.buyerName || '-',
        parseFloat(mov.commissionAmount || 0).toLocaleString(),
        mov.paid ? 'Pagada' : 'Pendiente',
        mov.paymentDate || '-',
        '-',
      ]),
      startY: 22,
      styles: { fontSize: 8 },
      headStyles: { fillColor: [22, 160, 133] },
    });
    doc.save('comisiones.pdf');
  };

  // Calcular totales
  const totalPagadas = comisionesOrdenadas.filter((c: any) => c.paid).reduce((sum: number, c: any) => sum + parseFloat(c.commissionAmount || 0), 0);
  const totalPendientes = comisionesOrdenadas.filter((c: any) => !c.paid).reduce((sum: number, c: any) => sum + parseFloat(c.commissionAmount || 0), 0);

  return (
    <>
      {/* Notificación de éxito */}
      {success && (
        <div className="bg-emerald-500/10 border border-emerald-500/20 text-emerald-600 px-4 py-3 rounded-lg flex items-center space-x-3 mb-4">
          <CheckCircle className="h-5 w-5" />
          <span className="font-medium">{success}</span>
        </div>
      )}
      {/* Filtros de búsqueda y exportar con estilo unificado */}
      <div className="flex flex-wrap items-center gap-3 mb-6 px-1">
        {/* Buscar */}
        <div className="relative w-64">
          <Search className="absolute left-2.5 top-1/2 transform -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground/70" />
          <Input
            placeholder="Buscar..."
            value={search}
            onChange={e => setSearch(e.target.value)}
            className="pl-8 bg-transparent border border-input hover:border-ring focus:border-ring rounded-[6px] h-8 text-[13px] text-foreground placeholder:text-muted-foreground/40 shadow-none transition-colors"
          />
        </div>

        {/* Vendedor */}
        <div className="relative">
          <select
            value={selectedVendedor}
            onChange={e => setSelectedVendedor(e.target.value)}
            className="bg-transparent border border-input hover:border-ring focus:border-ring rounded-[6px] h-8 text-[13px] text-foreground shadow-none transition-colors pl-3 pr-8 appearance-none cursor-pointer min-w-[150px]"
            style={{
              backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 12 12'%3E%3Cpath fill='%238A8F98' d='M6 9L1 4h10z'/%3E%3C/svg%3E")`,
              backgroundRepeat: 'no-repeat',
              backgroundPosition: 'right 0.5rem center',
              backgroundSize: '10px'
            }}
          >
            {uniqueVendedores.map(v => <option key={v} value={v}>{v}</option>)}
          </select>
        </div>

        <div className="flex-1" />

        {/* Exportar */}
        <TableExportMenu
          onExcel={exportToExcel}
          onCSV={exportToCSV}
          onPDF={exportToPDF}
        />
      </div>
      {/* Periodo de Consulta fuera de cualquier Card */}
      <TablePeriodSelector
        selectedMonth={selectedMonth}
        selectedYear={selectedYear}
        onMonthChange={setSelectedMonth}
        onYearChange={setSelectedYear}
        months={meses}
        label="Período de Consulta"
      />

      {/* Tarjetas de resumen de comisiones */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
        <Card className="bg-card border border-emerald-500/20 shadow-none rounded-lg overflow-hidden">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-[13px] font-medium text-muted-foreground">Comisiones Pagadas</CardTitle>
            <CheckCircle className="h-4 w-4 text-emerald-500" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-emerald-500">Q. {totalPagadas.toLocaleString(undefined, { minimumFractionDigits: 2 })}</div>
            <p className="text-[11px] text-muted-foreground mt-1">Total de comisiones pagadas</p>
          </CardContent>
        </Card>
        <Card className="bg-card border border-amber-500/20 shadow-none rounded-lg overflow-hidden">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-[13px] font-medium text-muted-foreground">Comisiones Pendientes</CardTitle>
            <Clock className="h-4 w-4 text-amber-500" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-amber-500">Q. {totalPendientes.toLocaleString(undefined, { minimumFractionDigits: 2 })}</div>
            <p className="text-[11px] text-muted-foreground mt-1">Total de comisiones pendientes</p>
          </CardContent>
        </Card>
      </div>

      <Card className="mb-8 bg-card border-border shadow-none rounded-lg overflow-hidden">
        <CardContent className="p-0">
          {/* Título y label de cantidad de registros */}
          <div className="px-4 py-3 border-b border-border/50">
            <div className="flex justify-between items-center">
              <span className="text-sm font-medium tracking-tight text-foreground">Comisiones ({filtered.length})</span>
              <span className="text-xs text-muted-foreground">Mostrando {filtered.length} registros</span>
            </div>
          </div>
          <div className="overflow-x-auto">
            <Table className="min-w-full text-sm bg-card">
              <TableHeader>
                <TableRow className="hover:bg-transparent border-b border-border/50">
                  <TableHead className="px-3 py-2 text-[11px] font-medium text-muted-foreground h-10">Orden</TableHead>
                  <TableHead className="px-3 py-2 text-[11px] font-medium text-muted-foreground h-10">Lote(s)</TableHead>
                  <TableHead className="px-3 py-2 text-[11px] font-medium text-muted-foreground h-10">Vendedor</TableHead>
                  <TableHead className="px-3 py-2 text-[11px] font-medium text-muted-foreground h-10">Fecha Venta</TableHead>
                  <TableHead className="px-3 py-2 text-[11px] font-medium text-muted-foreground h-10">Comprador</TableHead>
                  <TableHead className="px-3 py-2 text-[11px] font-medium text-muted-foreground h-10">Comisión</TableHead>
                  <TableHead className="px-3 py-2 text-[11px] font-medium text-muted-foreground h-10">Estado</TableHead>
                  <TableHead className="px-3 py-2 text-[11px] font-medium text-muted-foreground h-10">Fecha Pago</TableHead>
                  <TableHead className="px-3 py-2 text-[11px] font-medium text-muted-foreground h-10">Recibo/Transferencia</TableHead>
                  <TableHead className="px-3 py-2 text-[11px] font-medium text-muted-foreground h-10">Acciones</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filtered.map((com, idx) => (
                  <TableRow key={com.id || idx} className="border-b border-border/50 hover:bg-muted/50 transition-colors">
                    <TableCell className="px-3 py-3 font-mono text-[13px] text-muted-foreground">{idx + 1}</TableCell>
                    <TableCell className="px-3 py-3 text-[13px] text-foreground">{com.lotNumber || '-'}</TableCell>
                    <TableCell className="px-3 py-3 text-[13px] text-foreground">{
                      (() => {
                        const vendedorObj = sellers.find((s: any) => s.account === com.sellerId || s.name === com.sellerName);
                        return vendedorObj ? vendedorObj.name : (com.sellerName || com.sellerId || '-');
                      })()
                    }</TableCell>
                    <TableCell className="px-3 py-3 text-[13px] text-foreground">
                      {formatGTDate(com.saleDate)}
                    </TableCell>
                    <TableCell className="px-3 py-3 text-[13px] text-foreground">{com.buyerName || '-'}</TableCell>
                    <TableCell className="px-3 py-3 font-mono text-[13px] text-emerald-500">Q. {parseFloat(com.commissionAmount || 0).toLocaleString()}</TableCell>
                    <TableCell className="px-3 py-3">
                      {com.paid ? (
                        <div className="flex items-center gap-1.5 text-emerald-500">
                          <CheckCircle className="h-3.5 w-3.5" />
                          <span className="text-[13px] font-medium">Pagada</span>
                        </div>
                      ) : (
                        <div className="flex items-center gap-1.5 text-amber-500">
                          <Clock className="h-3.5 w-3.5" />
                          <span className="text-[13px] font-medium">Pendiente</span>
                        </div>
                      )}
                    </TableCell>
                    <TableCell className="px-3 py-3 text-[13px] text-foreground">{
                      formatGTDate(com.paymentDate)
                    }</TableCell>
                    <TableCell className="px-3 py-3 text-[13px] text-foreground">{
                      [com.transferencia, com.recibo].filter(Boolean).join(' / ') || '-'
                    }</TableCell>
                    <TableCell className="px-3 py-3">
                      <div className="flex items-center gap-2 justify-center">
                        {/* Si está pagada, mostrar botón de recibo */}
                        {com.paid && (
                          <DownloadReceiptButton
                            empresaInfo={{
                              razonSocial: config.razonSocial || 'Bimobi',
                              nit: config.nit,
                              direccionCompleta: config.direccionCompleta || config.ubicacion || 'Guatemala',
                              telefono1: config.telefono1 || '',
                              telefono2: config.telefono2,
                              email: config.email,
                              logoUrl: config.logoUrl
                            }}
                            detalleRecibo={{
                              tipoRecibo: 'pago_comision',
                              vendedor: {
                                nombre: (() => {
                                  const vendedorObj = sellers.find((s: any) => s.account === com.sellerId || s.name === com.sellerName);
                                  return vendedorObj ? vendedorObj.name : (com.sellerName || com.sellerId || 'Sin nombre');
                                })()
                              },
                              lotes: com.lotNumber || '-',
                              comprador: com.buyerName || '-',
                              fechaVentaOriginal: com.saleDate,
                              montoComision: parseFloat(com.commissionAmount || 0),
                              fechaPago: com.paymentDate || '',
                              transferencia: com.transferencia || '-',
                              boleta: com.recibo || '-'
                            } as Omit<DetalleComision, 'numeroRecibo' | 'fechaEmision'>}
                            fileName={`Recibo-Comision-${com.lotNumber || com.id}`}
                            onSuccess={(numeroRecibo) => {
                              console.log('Recibo de comisión generado:', numeroRecibo);
                            }}
                          />
                        )}

                        {/* Si no está pagada, mostrar botón de pagar */}
                        {!com.paid && (
                          <Button
                            size="sm"
                            variant="outline"
                            className="bg-transparent border border-input hover:bg-accent text-foreground rounded-[6px] h-8 text-[13px] font-medium transition-colors px-3"
                            onClick={() => { setModalOpen(true); setPagoComision(com); }}
                          >
                            Pagar comisión
                          </Button>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
          {/* Modal para registrar pago de comisión */}
          <Dialog open={modalOpen} onOpenChange={(v: boolean) => { if (!v) { setModalOpen(false); setPagoComision(null); setForm({ numeroDocumento: '', transferencia: '', boleta: '', fecha: dayjs().format('YYYY-MM-DD') }); setError(null); setSuccess(null); } }}>
            <DialogContent className="max-w-md bg-background border border-border rounded-xl">
              <DialogHeader>
                <DialogTitle className="text-foreground">Registrar pago de comisión</DialogTitle>
              </DialogHeader>
              <div className="space-y-4 mt-2">
                {/* Mostrar información de los lotes */}
                {pagoComision && (
                  <div className="mb-2 text-sm text-foreground font-medium border border-border rounded-md px-3 py-2 bg-muted/50">
                    <span className="block text-xs text-muted-foreground mb-1">Lote(s) involucrados:</span>
                    <span className="text-base text-foreground">{Array.isArray(pagoComision.lotes) ? pagoComision.lotes.join(', ') : (pagoComision.lotNumber || pagoComision.lotes || '-')}</span>
                  </div>
                )}
                <div>
                  <Label htmlFor="numeroDocumento" className="block text-xs font-medium mb-1 text-foreground">
                    No. Doc. del Banco <span className="text-red-500">*</span>
                    <span className="text-xs text-muted-foreground block mt-1 font-normal">Número de documento que aparece en el estado de cuenta del banco</span>
                  </Label>
                  <Input id="numeroDocumento" type="number" inputMode="numeric" pattern="[0-9]*" value={form.numeroDocumento} onChange={e => setForm(f => ({ ...f, numeroDocumento: e.target.value.replace(/\D/g, '') }))} placeholder="Ej. 9186345" required className="border border-input focus:border-ring bg-background text-foreground" />
                </div>
                <div>
                  <Label htmlFor="transferencia" className="block text-xs font-medium mb-1 text-foreground">
                    No. Autorización <span className="text-muted-foreground">(Opcional)</span>
                    <span className="text-xs text-muted-foreground block mt-1 font-normal">Número de autorización o transferencia (diferente al No. Doc. del banco)</span>
                  </Label>
                  <Input id="transferencia" type="number" inputMode="numeric" pattern="[0-9]*" value={form.transferencia} onChange={e => setForm(f => ({ ...f, transferencia: e.target.value.replace(/\D/g, '') }))} placeholder="Ej. 123456789" className="border border-input focus:border-ring bg-background text-foreground" />
                </div>
                {/* Campo boleta oculto pero mantenido en estado para compatibilidad histórica */}
                <div>
                  <Label htmlFor="fecha" className="block text-xs font-medium mb-1 text-foreground">Fecha de pago <span className="text-red-500">*</span></Label>
                  <Input type="date" id="fecha" value={form.fecha} onChange={e => setForm(f => ({ ...f, fecha: e.target.value }))} required className="border border-input focus:border-ring bg-background text-foreground [color-scheme:light] dark:[color-scheme:dark]" />
                </div>
                {error && <div className="text-red-600 text-sm mt-2">{error}</div>}
              </div>
              <div className="flex justify-end gap-2 mt-4">
                <CancelButton onClick={() => { setModalOpen(false); setPagoComision(null); setForm({ numeroDocumento: '', transferencia: '', boleta: '', fecha: dayjs().format('YYYY-MM-DD') }); setError(null); setSuccess(null); }} />
                <SaveButton onClick={handleGuardarPago} />
              </div>
            </DialogContent>
          </Dialog>
        </CardContent>
      </Card>
    </>
  );
}